import { LitElement, css, html, nothing } from "lit";
import { fa, onceParentAvailable } from "../../utils/widgets.js";
import { faClock, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { pickBestPerMonth } from "../../utils/picture.js";
import { getDistance, rad2deg } from "../../utils/utils.js";
import { TILES_PICTURES_ZOOM } from "../../utils/map.js";
import { panel } from "../styles.js";

// Radius (in degrees, ~15 m) used both to look for other captures and to decide
// whether the newly selected picture is still "the same place" as the listing.
const TT_RADIUS_DEG = 0.00015;
// Max amount of surrounding pictures to fetch through the API fallback.
const TT_LIMIT = 1000;
// Half-size (px) of the box used to query already-rendered map tiles around the point.
const TT_TILE_QUERY_PX = 45;
// Max time (ms) to wait for the map to finish loading tiles before falling back to the API.
const TT_MAP_IDLE_TIMEOUT = 4000;
// Angular tolerance (degrees) for considering another capture as "looking the same
// way" as the current one. Total window is twice this value.
const TT_HEADING_TOLERANCE_DEG = 45;
// Debounce (ms) before rebuilding the listing while the user rotates a 360° view.
const TT_ROTATE_DEBOUNCE = 300;

/**
 * Smallest absolute difference between two headings, in [0, 180] degrees.
 * @private
 */
function headingDiff(a, b) {
	return Math.abs(((a - b + 540) % 360) - 180);
}

/**
 * TimeTravel component lets the user jump between captures of the same place taken on different months.
 *
 * When the current picture's location has captures on 2 or more distinct months,
 * a clock button shows up. Clicking it opens a thumbnail filmstrip (one best
 * picture per month, oldest first); clicking a thumbnail selects that capture.
 *
 * Nearby captures are read from the already-loaded map vector tiles when a map is
 * available and zoomed in (no extra API call, and the active map filters apply);
 * otherwise they are fetched from the API (e.g. in the Photo Viewer).
 *
 * Only captures looking roughly the same way as the current view are kept (to avoid
 * confusing opposite-direction pictures); for a 360° current picture the reference
 * is the direction the user is looking at, and the listing is rebuilt on rotation.
 * The widget is hidden on mobile-sized containers (see Viewer widget setup).
 *
 * @class Panoramax.components.ui.TimeTravel
 * @element pnx-time-travel
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @example
 * ```html
 * <pnx-time-travel ._parent=${viewer} />
 * ```
 */
export default class TimeTravel extends LitElement {
	/** @private */
	static styles = [ panel, css`
		:host { pointer-events: none; }
		:host > * { pointer-events: auto; }

		/* Toggle button: same look as the other viewer widgets. */
		pnx-button::part(btn) { border-radius: 8px; height: 26px; width: 26px; }
		pnx-button[size="xl"]::part(btn) { border-radius: 8px; height: 38px; width: 38px; }
		.pnx-tt-count {
			position: absolute;
			top: -4px;
			right: -4px;
			min-width: 16px;
			height: 16px;
			padding: 0 3px;
			box-sizing: border-box;
			border-radius: 8px;
			background: var(--widget-bg-active);
			color: var(--widget-font-active);
			font: 11px/16px var(--font-family);
			text-align: center;
		}

		/* Filmstrip: reuse the classic panel look (white background, theme font). */
		.pnx-tt-strip {
			position: relative;
			display: flex;
			align-items: stretch;
			gap: 6px;
			padding: 8px;
			min-width: unset;
			max-width: 50vw;
			border-radius: 20px;
		}
		.pnx-tt-items {
			display: flex;
			gap: 8px;
			overflow-x: scroll;
			scrollbar-gutter: stable;
			padding-bottom: 10px;
		}
		.pnx-tt-item {
			flex: 0 0 auto;
			width: 130px;
			padding: 0;
			border: 2px solid var(--grey-pale);
			border-radius: 10px;
			background: var(--widget-bg);
			color: var(--widget-font);
			cursor: pointer;
			overflow: hidden;
			font-family: var(--font-family);
		}
		.pnx-tt-item img {
			display: block;
			width: 100%;
			height: 75px;
			object-fit: cover;
			background: var(--widget-bg-inactive);
		}
		.pnx-tt-date { display: block; padding: 4px; font-size: 10px; text-align: center; white-space: nowrap; }
		.pnx-tt-item:hover { border-color: var(--widget-bg-active); }
		.pnx-tt-item.pnx-tt-current { border-color: var(--widget-bg-active); }
		.pnx-tt-close::part(btn) { height: 100% !important; }
	` ];

	/**
	 * Component properties.
	 * @memberof Panoramax.components.ui.TimeTravel#
	 * @type {Object}
	 * @property {string} [size=xl] Button size (md, xl)
	 */
	static properties = {
		size: {type: String, reflect: true},
		_entries: {state: true},
		_current: {state: true},
		_open: {state: true},
		_dragMouseDown: {state: true},
		_dragStartX: {state: true},
		_dragScrollLeft: {state: true},
	};

	constructor() {
		super();
		this.size = "xl";
		this._entries = [];
		this._current = null;
		this._open = false;
		this._thumbs = new Map(); // picId -> thumbnail URL (lazily resolved)
		this._anchor = null;      // [lon, lat] where the current listing was built
		this._collecting = false;
		this._rawPics = [];       // last collected nearby pictures (before direction filtering)
		this._currentIs360 = false;
		this._rotateTimer = null;
		this._dragMouseDown = false;
	}

	/** @private */
	connectedCallback() {
		super.connectedCallback();
		onceParentAvailable(this).then(() => {
			this._parent.onceReady().then(() => {
				this._parent.psv.addEventListener("picture-loaded", () => this._onPictureChange());
				// On a 360° capture, the "current direction" is where the user looks, so
				// re-filter (debounced) the listing when they rotate to another direction.
				this._parent.psv.addEventListener("view-rotated", () => this._onViewRotated());
			});
			this._parent.onceFirstPicLoaded().then(() => this._onPictureChange());
		});
	}

	/** @private */
	disconnectedCallback() {
		super.disconnectedCallback();
		if(this._rotateTimer) { clearTimeout(this._rotateTimer); }
	}

	/** @private Debounced rebuild of the listing while rotating a 360° view. */
	_onViewRotated() {
		if(!this._currentIs360 || !this._rawPics.length) { return; }
		if(this._rotateTimer) { clearTimeout(this._rotateTimer); }
		this._rotateTimer = setTimeout(() => {
			this._rotateTimer = null;
			this._rebuildEntries();
		}, TT_ROTATE_DEBOUNCE);
	}

	/**
	 * Reacts to a picture change: keeps the current listing while we stay in the
	 * same nearby range (so navigation stays stable, GMaps-like), otherwise
	 * re-anchors and collects the surrounding captures again.
	 * @private
	 */
	_onPictureChange() {
		const meta = this._parent?.psv?.getPictureMetadata?.();
		const coords = meta?.gps || meta?.geometry?.coordinates;
		const date = meta?.properties?.datetime;
		if(!coords || !date) { this._reset(); return; }

		this._current = date.slice(0, 7);
		this._currentIs360 = meta?.properties?.["pers:interior_orientation"]?.["field_of_view"] === 360;

		// Same place as the current listing -> keep the collected pictures, but the
		// reference direction may have changed, so re-filter and refresh the highlight.
		if(this._anchor && (this._rawPics.length || this._collecting)
			&& getDistance(coords, this._anchor) <= TT_RADIUS_DEG) {
			this._rebuildEntries();
			return;
		}

		this._anchor = coords;
		this._collecting = true;
		this._collect(coords)
			.then(pics => { this._rawPics = pics; this._rebuildEntries(); })
			.catch(e => { console.warn("Time-travel collect failed:", e); this._rawPics = []; this._entries = []; })
			.finally(() => { this._collecting = false; this.requestUpdate(); });
	}

	/**
	 * Rebuilds the per-month listing from the last collected pictures, keeping only
	 * captures looking roughly the same way as the current view (see feedback on
	 * confusing East/West mixes). A 360° capture "looks" every way, so it is always
	 * kept; for a 360° current picture the reference is the direction the user is
	 * currently looking at.
	 * @private
	 */
	_rebuildEntries() {
		if(!this._anchor) { this._entries = []; return; }
		this._entries = pickBestPerMonth(this._filterByDirection(this._rawPics), this._anchor);
		this.requestUpdate();
	}

	/** @private Reference heading (degrees) the listing should look towards, or null. */
	_currentRefDir() {
		const psv = this._parent?.psv;
		if(!psv?.getPictureOriginalHeading) { return null; }
		const heading = psv.getPictureOriginalHeading() || 0;
		if(this._currentIs360) {
			const yaw = psv.getPosition?.()?.yaw || 0;
			return (heading + rad2deg(yaw) + 360) % 360;
		}
		return (heading + 360) % 360;
	}

	/** @private Keeps only pictures looking within tolerance of the current direction. */
	_filterByDirection(pics) {
		const ref = this._currentRefDir();
		if(ref === null) { return pics; }
		return (pics || []).filter(p =>
			// eslint-disable-next-line eqeqeq
			p.is360 || p.heading == null || headingDiff(p.heading, ref) <= TT_HEADING_TOLERANCE_DEG
		);
	}

	/** @private */
	_reset() {
		this._entries = [];
		this._rawPics = [];
		this._anchor = null;
		this._open = false;
	}

	/** @private Id of the map layer showing individual pictures, if any. */
	_picLayerId() {
		const msc = this._parent?.mapStyleComposer;
		const range = msc?.layerRanges?.panoramax?.[msc?.panoramax];
		return range?.layers?.find(l => l.id.endsWith("_pictures"))?.id || null;
	}

	/**
	 * Collects nearby pictures, preferring already-loaded map vector tiles (which
	 * respect active map filters and need no API call), falling back to the API.
	 * @private
	 */
	_collect(coords) {
		const map = this._parent?.map;
		const layerId = this._picLayerId();
		if(map && layerId) {
			// The map (incl. the mini-map in picture view) renders the pictures and
			// respects the active map filters, so prefer it. It may still be loading
			// tiles right after recentering on the new picture, so poll for a short
			// while before falling back to the API.
			return this._pollTiles(coords, map, layerId)
				.then(pics => pics.length ? pics : this._collectFromApi(coords));
		}
		return this._collectFromApi(coords);
	}

	/**
	 * Polls the rendered map tiles around the point until pictures show up (they
	 * may still be loading) or a timeout elapses. More reliable than `idle` /
	 * `areTilesLoaded` here, since Panoramax picture tiles use a custom protocol.
	 * @private
	 */
	_pollTiles(coords, map, layerId) {
		const deadline = Date.now() + TT_MAP_IDLE_TIMEOUT;
		return new Promise(resolve => {
			const tryOnce = () => {
				if(map.getLayer?.(layerId) && map.getZoom?.() >= TILES_PICTURES_ZOOM) {
					const pics = this._collectFromTiles(coords, map, layerId);
					if(pics.length) { resolve(pics); return; }
				}
				if(Date.now() >= deadline) { resolve([]); return; }
				setTimeout(tryOnce, 300);
			};
			tryOnce();
		});
	}

	/** @private */
	_collectFromTiles(coords, map, layerId) {
		let box;
		try {
			const pt = map.project(coords);
			box = [[pt.x - TT_TILE_QUERY_PX, pt.y - TT_TILE_QUERY_PX], [pt.x + TT_TILE_QUERY_PX, pt.y + TT_TILE_QUERY_PX]];
		}
		catch(e) { return []; }

		const seen = new Set();
		const out = [];
		for(const f of map.queryRenderedFeatures(box, { layers: [layerId] })) {
			const id = f.properties?.id;
			const ts = f.properties?.ts;
			const c = f.geometry?.coordinates;
			if(!id || !ts || !c || seen.has(id)) { continue; }
			if(getDistance(coords, c) > TT_RADIUS_DEG) { continue; }
			seen.add(id);
			out.push({
				id,
				seqId: f.properties.sequence || null,
				coords: c,
				date: String(ts).slice(0, 10),
				density: Number(f.properties.h_pixel_density) || 0,
				is360: f.properties.type === "equirectangular",
				// eslint-disable-next-line eqeqeq
				heading: f.properties.heading != null ? Number(f.properties.heading) : null,
				thumb: null,
			});
		}
		return out;
	}

	/** @private */
	_collectFromApi(coords) {
		const [lon, lat] = coords;
		const api = this._parent.getAPI();
		return api.getPicturesAroundCoordinates(lat, lon, TT_RADIUS_DEG, TT_LIMIT).then(fc => (fc?.features || []).map(f => {
			const io = f.properties?.["pers:interior_orientation"] || {};
			const fov = io.field_of_view;
			const width = io.sensor_array_dimensions?.[0];
			let thumb = null;
			try { thumb = api.findThumbnailInPictureFeature(f); }
			catch(e) { /* keep null */ }
			const azimuth = f.properties?.["view:azimuth"];
			return {
				id: f.id,
				seqId: f.collection,
				coords: f.geometry.coordinates,
				date: f.properties.datetime,
				density: (width && fov) ? width / fov : 0,
				is360: fov === 360,
				// eslint-disable-next-line eqeqeq
				heading: azimuth != null ? Number(azimuth) : null,
				thumb,
			};
		}));
	}

	/** @private Resolves the thumbnail URL for an entry (async for tile entries). */
	_thumbFor(entry) {
		if(entry.thumb) { return entry.thumb; }
		if(this._thumbs.has(entry.id)) { return this._thumbs.get(entry.id); }
		this._thumbs.set(entry.id, null);
		Promise.resolve(this._parent.getAPI().getPictureThumbnailURL(entry.id, entry.seqId))
			.then(u => { this._thumbs.set(entry.id, u); this.requestUpdate(); })
			.catch(() => { /* leave without thumbnail */ });
		return null;
	}

	/** @private */
	_monthLabel(entry) {
		const d = new Date(entry.date);
		return isNaN(d.getTime())
			? entry.month
			: d.toLocaleDateString(this._parent?.lang || undefined, { year: "numeric", month: "short" });
	}

	/** @private Shows the map preview marker at an entry's location on hover. */
	_hoverOn(entry) {
		const map = this._parent?.map;
		if(map && map._picMarkerPreview && entry.coords) {
			// Point the marker the way the picture was captured (its heading), rather
			// than always North.
			try { map._picMarkerPreview.setLngLat(entry.coords).setRotation(entry.heading || 0).addTo(map); }
			catch(e) { /* ignore */ }
		}
	}

	/** @private */
	_hoverOff() {
		const map = this._parent?.map;
		if(map && map._picMarkerPreview) {
			try { map._picMarkerPreview.remove(); }
			catch(e) { /* ignore */ }
		}
	}

	/** @private */
	_startDragging(e) {
		e.preventDefault();
		const slider = this.shadowRoot.querySelector(".pnx-tt-items");
		this._dragMouseDown = true;
		this._dragStartX = e.pageX - slider.offsetLeft;
		this._dragScrollLeft = slider.scrollLeft;
	}

	/** @private */
	_stopDragging() {
		this._dragMouseDown = false;
	}

	/** @private */
	_moveDragging(e) {
		e.preventDefault();
		if(!this._dragMouseDown) { return; }
		const slider = this.shadowRoot.querySelector(".pnx-tt-items");
		const x = e.pageX - slider.offsetLeft;
		const scroll = x - this._dragStartX;
		slider.scrollLeft = this._dragScrollLeft - scroll;
	}

	/** @private */
	_select(entry) {
		this._parent.psv._keepSameAngle = true;
		this._parent.select(entry.seqId, entry.id);
	}

	/** @private */
	render() {
		// Only relevant when the place has been captured on 2+ distinct months.
		const has = this._entries && this._entries.length >= 2;

		if(has && !this._open) {
			return html`
				<pnx-button
					kind="superflat"
					size=${this.size}
					class="pnx-print-hidden"
					style="vertical-align: middle"
					title=${this._parent._t?.pnx.time_travel}
					@click=${() => this._open = true}
				>
					${fa(faClock, { styles: { "height": "20px" } })}
					<span class="pnx-tt-count">${this._entries.length}</span>
				</pnx-button>
			`;
		}

		if(has && this._open) {
			return html`
				<div
					class="pnx-tt-strip pnx-panel pnx-print-hidden"
					@mouseleave=${() => { this._hoverOff(); this._stopDragging(); }}
					@mousemove=${e => this._moveDragging(e)}
					@mousedown=${e => this._startDragging(e)}
					@mouseup=${e => this._stopDragging(e)}
				>
					<div class="pnx-tt-items">
						${this._entries.map(entry => {
							const thumb = this._thumbFor(entry);
							const classes = "pnx-tt-item" + (entry.month === this._current ? " pnx-tt-current" : "");
							return html`
								<button
									class=${classes}
									title=${this._monthLabel(entry)}
									@click=${() => this._select(entry)}
									@mouseenter=${() => this._hoverOn(entry)}
								>
									${thumb ? html`<img src=${thumb} alt=${entry.month} loading="lazy" draggable="false" />` : nothing}
									<span class="pnx-tt-date">${this._monthLabel(entry)}</span>
								</button>
							`;
						})}
					</div>
					<pnx-button
						kind="superflat"
						size="md"
						class="pnx-tt-close"
						title=${this._parent._t?.pnx.time_travel_close}
						@click=${() => this._open = false}
					>
						${fa(faChevronDown)}
					</pnx-button>
				</div>
			`;
		}

		return nothing;
	}
}

customElements.define("pnx-time-travel", TimeTravel);
